'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import NextLink from 'next/link';
import NextImage from 'next/image';
import { CalendarBlank, Clock, Eye, Tag, CaretRight, Article } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Header, Footer } from '@/components/layout';
import { blogApi, type BlogPostPreview } from '@/lib/api/blog';
import { JsonLd } from '@/components/seo/JsonLd';
import { generateBreadcrumbSchema } from '@/lib/seo/json-ld';

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('ru-RU', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

function BlogCard({ post, index }: { post: BlogPostPreview; index: number }) {
  const isFeatured = index === 0;

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.4 }}
      className={cn(
        'group relative rounded-card overflow-hidden',
        'bg-[var(--bg-elevated)] border border-[var(--border-default)]',
        // shadow-[var(--primary)]/5 удалён как мёртвый: Tailwind v4 читает
        // shadow-[var(--…)] как значение тени, а не как цвет, и соседний
        // shadow-xl побеждал его в каскаде — фиолетового ореола здесь никогда
        // не было, рисовалась обычная чёрная тень (ответ 35, вариант В отклонён).
        'hover:border-[var(--primary)]/30 hover:shadow-float',
        'transition-all duration-300',
        isFeatured && 'md:col-span-2 md:grid md:grid-cols-2',
      )}
    >
      <NextLink href={`/blog/${post.slug}`} className="block">
        {/* Image */}
        <div className={cn(
          'relative overflow-hidden bg-[var(--surface-secondary)]',
          isFeatured ? 'aspect-[16/10] md:aspect-auto md:h-full' : 'aspect-[16/10]',
        )}>
          {post.image_url ? (
            <NextImage
              src={post.image_url}
              alt={post.title}
              fill
              className="object-cover transition-transform duration-500 group-hover:scale-105"
              sizes={isFeatured ? '(max-width: 768px) 100vw, 50vw' : '(max-width: 768px) 100vw, 33vw'}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Article className="w-12 h-12 text-[var(--text-tertiary)]" />
            </div>
          )}
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>

        {/* Content */}
        <div className={cn('p-5', isFeatured && 'md:p-8 md:flex md:flex-col md:justify-center')}>
          {/* Tags */}
          {post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {post.tags.slice(0, 3).map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-card text-xs font-medium bg-[var(--primary)]/10 text-[var(--primary)]">
                  <Tag className="w-3 h-3" /> {tag}
                </span>
              ))}
            </div>
          )}

          <h2 className={cn(
            'font-heading font-bold text-[var(--text-primary)] leading-tight',
            'group-hover:text-[var(--primary)] transition-colors',
            isFeatured ? 'text-xl md:text-2xl' : 't-card-title',
          )}>
            {post.title}
          </h2>

          {post.excerpt && (
            <p className={cn(
              'mt-2 text-[var(--text-secondary)] line-clamp-2',
              isFeatured ? 'text-base' : 'text-sm',
            )}>
              {post.excerpt}
            </p>
          )}

          {/* Meta */}
          <div className="flex items-center gap-4 mt-4 text-xs text-[var(--text-tertiary)]">
            <span className="flex items-center gap-1">
              <CalendarBlank className="w-3.5 h-3.5" />
              {formatDate(post.published_at)}
            </span>
            {post.reading_time > 0 && (
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {post.reading_time} мин
              </span>
            )}
            <span className="flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" />
              {post.view_count}
            </span>
          </div>

          {/* Read more */}
          <div className="mt-4 flex items-center gap-1 text-sm font-medium text-[var(--primary)] opacity-0 group-hover:opacity-100 transition-opacity">
            Читать далее <CaretRight className="w-4 h-4" weight="bold" />
          </div>
        </div>
      </NextLink>
    </motion.article>
  );
}

export function BlogListClient() {
  const [page, setPage] = useState(1);
  const [activeTag, setActiveTag] = useState<string | undefined>();

  const { data, isLoading } = useQuery({
    queryKey: ['blog', page, activeTag],
    queryFn: () => blogApi.getList(page, 12, activeTag),
    staleTime: 60000,
  });

  const { data: tags = [] } = useQuery({
    queryKey: ['blog-tags'],
    queryFn: () => blogApi.getTags(),
    staleTime: 300000,
  });

  const posts = data?.data || [];
  const totalPages = data?.total_pages || 1;

  return (
    <main className="min-h-screen bg-[var(--bg-primary)]">
      <JsonLd data={generateBreadcrumbSchema([
        { name: 'Главная', href: 'https://aziatochka.ru' },
        { name: 'Блог', href: 'https://aziatochka.ru/blog' },
      ])} />

      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-16">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-[var(--text-primary)]">
            Блог <span className="text-[var(--primary)]">Азиаточки</span>
          </h1>
          <p className="mt-3 text-base text-[var(--text-secondary)] max-w-2xl mx-auto">
            Статьи о японской кухне, рецепты, советы и новости
          </p>
        </motion.div>

        {/* Tags filter */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center mb-10">
            <button
              onClick={() => { setActiveTag(undefined); setPage(1); }}
              className={cn(
                'px-4 py-2 rounded-pill text-sm font-medium transition-all',
                !activeTag
                  ? 'bg-[var(--primary)] text-white shadow-raised'
                  : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)] border border-[var(--border-default)] hover:border-[var(--primary)]/30'
              )}
            >
              Все
            </button>
            {tags.map(tag => (
              <button
                key={tag}
                onClick={() => { setActiveTag(tag); setPage(1); }}
                className={cn(
                  'px-4 py-2 rounded-pill text-sm font-medium transition-all',
                  activeTag === tag
                    ? 'bg-[var(--primary)] text-white shadow-raised'
                    : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)] border border-[var(--border-default)] hover:border-[var(--primary)]/30'
                )}
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        {/* Posts grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={cn(
                'rounded-card bg-[var(--bg-elevated)] border border-[var(--border-default)] overflow-hidden animate-pulse',
                i === 0 && 'md:col-span-2',
              )}>
                <div className="aspect-[16/10] bg-[var(--surface-secondary)]" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-[var(--surface-secondary)] rounded w-3/4" />
                  <div className="h-3 bg-[var(--surface-secondary)] rounded w-full" />
                  <div className="h-3 bg-[var(--surface-secondary)] rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <Article className="w-16 h-16 text-[var(--text-tertiary)] mx-auto mb-4" />
            <p className="text-base text-[var(--text-secondary)]">Статей пока нет</p>
            <p className="text-sm text-[var(--text-tertiary)] mt-1">Скоро здесь появятся интересные материалы</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post, i) => (
              <BlogCard key={post.id} post={post} index={i} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-12">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={cn(
                  'w-10 h-10 rounded-card text-sm font-medium transition-all',
                  p === page
                    ? 'bg-[var(--primary)] text-white shadow-raised'
                    : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)] border border-[var(--border-default)] hover:border-[var(--primary)]/30'
                )}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>

      <Footer className="bg-[var(--bg-primary)]" />
    </main>
  );
}
